import axios from 'axios'

import { routerApolloClient } from '@/shared/graphql/client'
import { DnaFileDetectionStatus } from '@/shared/graphql/router/graphql'

import { GENERIC_MESSAGE, readDetectorError, sleep } from './errors'
import { readDetection, type DetectionResult } from './findings'
import {
  CANCEL_DNA_FILE_DETECTION_MUTATION,
  CREATE_DNA_DETECTION_MULTIPART_UPLOAD_MUTATION,
  DNA_FILE_DETECTION_QUERY,
  SIGN_DNA_DETECTION_UPLOAD_PARTS_MUTATION,
  START_DNA_FILE_DETECTION_MUTATION,
} from './operations'

const MAX_SIGNED_PARTS = 32
const POLL_MS = 2000
const POLL_TIMEOUT_MS = 12 * 60_000

export type UploadPhase =
  | { kind: 'uploading'; percentage: number }
  | { kind: 'analyzing' }

export class CheckAborted extends Error {}

const rethrow = (error: unknown): never => {
  throw new Error(readDetectorError(error).message)
}

const checkSignal = (signal: AbortSignal) => {
  if (signal.aborted) throw new CheckAborted()
}

export async function checkFile(
  file: File,
  onPhase: (phase: UploadPhase) => void,
  signal: AbortSignal
): Promise<DetectionResult> {
  if (!file.size) throw new Error('This file is empty.')

  let jobId: string | null = null
  const cancel = () => {
    if (!jobId) return
    routerApolloClient
      .mutate({
        mutation: CANCEL_DNA_FILE_DETECTION_MUTATION,
        variables: { id: jobId },
        fetchPolicy: 'no-cache',
      })
      .catch(() => {})
  }
  signal.addEventListener('abort', cancel, { once: true })

  try {
    const { data } = await routerApolloClient
      .mutate({
        mutation: CREATE_DNA_DETECTION_MULTIPART_UPLOAD_MUTATION,
        variables: { input: { filename: file.name, sizeBytes: file.size } },
        fetchPolicy: 'no-cache',
      })
      .catch(rethrow)
    const upload = data?.createDnaDetectionMultipartUpload
    if (!upload) throw new Error(GENERIC_MESSAGE)
    jobId = upload.id
    checkSignal(signal)

    const urls = new Map(
      upload.parts.map((part) => [part.partNumber, part.url])
    )
    onPhase({ kind: 'uploading', percentage: 0 })
    for (let part = 1; part <= upload.partCount; part++) {
      if (!urls.has(part)) {
        const partNumbers = Array.from(
          { length: Math.min(MAX_SIGNED_PARTS, upload.partCount - part + 1) },
          (_, index) => part + index
        )
        const signed = await routerApolloClient
          .mutate({
            mutation: SIGN_DNA_DETECTION_UPLOAD_PARTS_MUTATION,
            variables: { input: { id: upload.id, partNumbers } },
            fetchPolicy: 'no-cache',
          })
          .catch(rethrow)
        for (const entry of signed.data?.signDnaDetectionUploadParts ?? [])
          urls.set(entry.partNumber, entry.url)
      }
      const url = urls.get(part)
      if (!url) throw new Error(GENERIC_MESSAGE)
      const start = (part - 1) * upload.partSizeBytes
      const end = Math.min(file.size, start + upload.partSizeBytes)
      await axios.put(url, file.slice(start, end), {
        signal,
        onUploadProgress: (event) => {
          onPhase({
            kind: 'uploading',
            percentage: Math.min(
              99,
              Math.round(((start + event.loaded) / file.size) * 100)
            ),
          })
        },
      })
      checkSignal(signal)
    }

    onPhase({ kind: 'analyzing' })
    await routerApolloClient
      .mutate({
        mutation: START_DNA_FILE_DETECTION_MUTATION,
        variables: { id: upload.id },
        fetchPolicy: 'no-cache',
      })
      .catch(rethrow)

    const deadline = Date.now() + POLL_TIMEOUT_MS
    for (;;) {
      checkSignal(signal)
      const { data: polled } = await routerApolloClient
        .query({
          query: DNA_FILE_DETECTION_QUERY,
          variables: { id: upload.id },
          fetchPolicy: 'no-cache',
        })
        .catch(rethrow)
      const job = polled?.dnaFileDetection
      if (job?.status === DnaFileDetectionStatus.Completed) {
        jobId = null
        const result = readDetection(job.result)
        if (!result) throw new Error(GENERIC_MESSAGE)
        return result
      }
      if (
        job?.status === DnaFileDetectionStatus.Failed ||
        job?.status === DnaFileDetectionStatus.Cancelled
      ) {
        jobId = null
        throw new Error(
          job.error?.code === 'MEDIA_TOO_LONG'
            ? 'This file is longer than four hours.'
            : 'We couldn’t analyze this file. Please try again.'
        )
      }
      if (Date.now() > deadline)
        throw new Error('Analysis is taking too long. Please try again.')
      await sleep(POLL_MS)
    }
  } catch (error) {
    if (axios.isCancel(error)) throw new CheckAborted()
    if (!(error instanceof CheckAborted)) cancel()
    throw error
  } finally {
    signal.removeEventListener('abort', cancel)
  }
}
